import Masthead from '@zambezi/sdk/masthead';
import { Nav, NavItem, NavMenu, NavMenuGroup, NavMenuItem } from '@zambezi/sdk/nav';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useBrand from '../../providers/BrandProvider';
import LogoutModal from '../LogoutModal';
import './Header.scss';

interface ILink {
  title: React.ReactNode | React.ReactNode[];
  href?: string;
  onClick?: () => void;
  isExternal?: boolean;
}

interface Group {
  title?: string;
  links: ILink[];
}

interface INavItem {
  title: React.ReactNode | React.ReactNode[];
  href?: string;
  isExternal?: boolean;
  groups?: Group[];
  onClick?: () => void;
}

export interface HeaderProps {
  title?: string;
  navItems?: INavItem[];
  isAuthenticated?: boolean;
  onLogout?: () => Promise<void> | void;
}

const Header: React.FC<HeaderProps> = ({
  title,
  navItems,
  isAuthenticated = false,
  onLogout
}: HeaderProps) => {
  const { longName, legalName } = useBrand();

  const [isLogoutModalActive, setIsLogoutModalActive] = useState<boolean>(false);
  const location = useLocation();

  const handleLogout = async () => {
    await onLogout?.();
    setIsLogoutModalActive(false);
  };

  return (
    <>
      <Masthead
        title={title}
        brand={longName}
        logoProps={{
          'aria-label': `${legalName} logo`
        }}
        nav={
          navItems && (
            <Nav aria-label="Main navigation">
              {navItems.map(
                ({ title, href, isExternal = false, groups, onClick }, sectionIndex) => (
                  <NavItem
                    key={sectionIndex}
                    renderLabel={
                      href
                        ? ({ onSelect, ...rest }) =>
                            isExternal ? (
                              <a href={href} {...rest}>
                                {title}
                              </a>
                            ) : (
                              <Link to={href} onClick={onSelect} {...rest}>
                                {title}
                              </Link>
                            )
                        : undefined
                    }
                    onSelect={onClick}
                  >
                    {title}
                    {groups && (
                      <NavMenu>
                        {groups.map((group, groupIndex) => (
                          <NavMenuGroup key={groupIndex} label={group.title}>
                            {group.links.map((link, linkIndex) => (
                              <NavMenuItem
                                key={linkIndex}
                                renderLabel={({ onSelect, ...rest }) => {
                                  if (link.href) {
                                    return link.isExternal ? (
                                      <a {...rest} href={link.href} onClick={onSelect}>
                                        {link.title}
                                      </a>
                                    ) : (
                                      <Link to={link.href} onClick={onSelect} {...rest}>
                                        {link.title}
                                      </Link>
                                    );
                                  }

                                  if (link.onClick) {
                                    return (
                                      <button {...rest} onClick={onSelect}>
                                        {link.title}
                                      </button>
                                    );
                                  }
                                }}
                                isSelected={location.pathname === link.href}
                                onSelect={link.onClick}
                              >
                                {link.title}
                              </NavMenuItem>
                            ))}
                          </NavMenuGroup>
                        ))}
                      </NavMenu>
                    )}
                  </NavItem>
                )
              )}
            </Nav>
          )
        }
        dark
      >
        {isAuthenticated && (
          <ul className="zb-masthead-nav-list">
            <li>
              <button
                className="zb-button zb-button-link | mss-logout-link"
                onClick={() => setIsLogoutModalActive(true)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="19" fill="none">
                  <path
                    fill="#fff"
                    fillRule="evenodd"
                    d="M2 7.3h10c1.1 0 2 .9 2 2v7c0 1.1-.9 2-2 2H2c-1.1 0-2-.9-2-2v-7c0-1.1.9-2 2-2Zm10 9.8c.4 0 .8-.4.8-.8v-7c0-.4-.4-.8-.8-.8H2c-.4 0-.8.4-.8.8v7c0 .4.4.8.8.8h10Z"
                    clipRule="evenodd"
                  />
                  <path
                    fill="#fff"
                    d="M2.9 5.4c.3 0 .6-.3.6-.6 0-1.3.6-2.5 1.8-3.1.6-.3 1.2-.5 1.8-.5 1.2 0 2.3.7 3 1.8.3.6.5 1.2.5 1.9 0 .3.3.6.6.6s.6-.3.6-.6c0-.9-.2-1.7-.6-2.5C10.4 1 8.9.1 7.2 0c-.9 0-1.7.2-2.5.6-1.6.9-2.5 2.5-2.4 4.2 0 .4.3.6.6.6Z"
                  />
                </svg>
                Log out
              </button>
            </li>
          </ul>
        )}
      </Masthead>
      {isAuthenticated && isLogoutModalActive && (
        <LogoutModal onConfirm={handleLogout} onCancel={() => setIsLogoutModalActive(false)} />
      )}
    </>
  );
};

export default Header;
